import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Send, MessageCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm Sarah, your sound therapy guide. What are you dealing with today — stress, sleep issues, pain, focus? I can help you find the right frequencies." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => Math.random().toString(36).slice(2));
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: msg }]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/chat`, { message: msg, session_id: sessionId });
      setMessages(prev => [...prev, { role: "assistant", text: r.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", text: "I'm having a quiet moment! Try asking about sound therapy for anxiety, sleep, focus, or pain relief." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Chat bubble with Sarah's face */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-20 right-4 z-50 w-16 h-16 rounded-full overflow-hidden chat-bubble ring-4 ring-primary/30"
          data-testid="chat-bubble"
          title="Chat with Sarah"
          style={{ animation: 'chat-pulse 2s ease-in-out infinite' }}
        >
          <img
            src={`${process.env.PUBLIC_URL}/sarah.jpg`}
            alt="Chat with Sarah"
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="width:100%;height:100%;background:#7C3AED;display:flex;align-items:center;justify-content:center;color:white;font-size:24px;font-weight:bold">S</div>'; }}
          />
        </button>
      )}

      {/* Chat window */}
      {open && (
        <div className="fixed bottom-20 right-4 z-50 w-80 sm:w-96 animate-slide-in-bottom" data-testid="chat-window">
          <div className="glass rounded-2xl overflow-hidden shadow-xl">
            {/* Header */}
            <div className="bg-primary text-primary-foreground px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <img src={`${process.env.PUBLIC_URL}/sarah.jpg`} alt="Sarah" className="w-8 h-8 rounded-full object-cover border border-white/30" />
                <div>
                  <p className="text-sm font-semibold">Sarah</p>
                  <p className="text-[10px] opacity-80">AI Wellness Guide</p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded" data-testid="chat-close">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* AI disclosure */}
            <div className="px-3 py-1.5 bg-primary/5 border-b border-border/30">
              <p className="text-[9px] text-muted-foreground text-center">
                Sarah is an AI assistant. For medical advice, consult a healthcare professional.
              </p>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="h-64 overflow-y-auto p-3 space-y-3 bg-background/50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${
                    m.role === "user"
                      ? "bg-primary text-primary-foreground rounded-br-md"
                      : "bg-card border border-border/50 rounded-bl-md"
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-card border border-border/50 px-3 py-2 rounded-2xl rounded-bl-md">
                    <div className="flex gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0s" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0.1s" }} />
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground/40 animate-bounce" style={{ animationDelay: "0.2s" }} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="p-3 border-t border-border/30 bg-card/80">
              <form onSubmit={e => { e.preventDefault(); send(); }} className="flex gap-2">
                <Input
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Ask about sound therapy..."
                  className="flex-1 text-sm bg-background/50"
                  data-testid="chat-input"
                />
                <Button type="submit" size="sm" disabled={loading || !input.trim()} data-testid="chat-send">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
